/**
 * Types for Presales Designer - Odoo Order Integration
 */

export interface OdooPartner {
  id: number;
  name: string;
}

export interface OdooProduct {
  id: number;
  sku: string | null;
  name: string;
  category: ProductCategory;
  categoryLabel: string;
}

export type ProductCategory =
  | 'cloud_connectivity'
  | 'enterprise_internet'
  | 'port'
  | 'last_mile'
  | 'datacenter_interconnect'
  | 'fwaas'
  | 'cpe'
  | 'ip_block'
  | 'sdwan_hub'
  | 'sdwan_branch'
  | 'other';

export interface OdooLocation {
  id: number;
  name: string;
}

export interface OdooOrderLine {
  id: number;
  product: OdooProduct;
  origin: OdooLocation | null;
  destination: OdooLocation | null;
  capacity: number;
  termMonths: number;
  correlation: string;
  serviceId: string | null;
  isProvisioned: boolean;
  description: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  lastMileDestination: string | null;
}

export interface CategoryGroup {
  label: string;
  lines: OdooOrderLine[];
  subtotal: number;
}

export interface OriginGroup {
  location: OdooLocation | null;
  lines: OdooOrderLine[];
  subtotal: number;
}

export interface CategoryStats {
  count: number;
  subtotal: number;
}

export interface OrderStats {
  totalLines: number;
  provisionedCount: number;
  pendingCount: number;
  byCategory: Record<string, CategoryStats>;
}

export interface OdooOrder {
  id: number;
  name: string;
  displayName: string;
  state: string;
  stateLabel: string;
  partner: OdooPartner | null;
  shippingPartner: OdooPartner | null;
  pid: string;
  cid: string;
  currency: { id: number; name: string } | null;
  amountTotal: number;
  dateOrder: string;
  salesUser: OdooPartner | null;
  salesTeam: OdooPartner | null;
  presales: OdooPartner | null;
  presalesEmail: string | null;
  orderLines: OdooOrderLine[];
  orderLineIds: number[];
  linesByCategory: Record<string, CategoryGroup>;
  linesByOrigin: Record<string, OriginGroup>;
  stats: OrderStats;
}

export interface OdooOrderResponse {
  success: boolean;
  data: OdooOrder;
  message?: string;
}

/**
 * Order List Types
 */
export interface OdooOrderListItem {
  id: number;
  name: string;
  displayName: string;
  state: string;
  stateLabel: string;
  partner: OdooPartner | null;
  shippingPartner: OdooPartner | null;
  amountTotal: number;
  currency: string;
  pid: string | null;
  cid: string | null;
  dateOrder: string;
  createdAt: string;
  // Processing status from ProcessedOdooOrders table
  processingStatus: 'processing' | 'completed' | 'failed' | 'pending_presales' | null;
  servicesCreated: number;
  tenantId: number | null;
  processedAt: string | null;
}

export interface OdooOrderListResponse {
  success: boolean;
  data: {
    orders: OdooOrderListItem[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  message?: string;
}

/**
 * Product Hierarchy Types - Parent/Child Assignment
 * 
 * Graph layout (4 columns):
 *   Last Mile/CPE → Port → FWaaS → Cloud/Internet
 *        (1)        (2)     (3)        (4)
 * 
 * Hierarchy:
 *   - Ports can have children: Last Mile, CPE, FWaaS, Cloud, Internet
 *   - FWaaS can have children: Cloud, Internet (FWaaS is both child of Port AND parent of services)
 */

// PRIMARY infrastructure (Ports, SD-WAN Hubs) - Column 2
export const PARENT_CATEGORIES: ProductCategory[] = ['port', 'sdwan_hub'];

// SECONDARY infrastructure (FWaaS) - Column 3, can have children
// FWaaS is both a child (of Port) AND a parent (of Cloud/Internet services)
export const FWAAS_CATEGORY: ProductCategory = 'fwaas';

// Categories that can be assigned to Ports (all children)
// Note: sdwan_branch is NOT a child category - branches are assigned via the dedicated hub assignment table
export const CHILD_CATEGORIES: ProductCategory[] = ['cloud_connectivity', 'enterprise_internet', 'last_mile', 'cpe', 'fwaas', 'ip_block'];

// Categories that can be assigned to FWaaS (FWaaS children)
export const FWAAS_CHILD_CATEGORIES: ProductCategory[] = ['cloud_connectivity', 'enterprise_internet'];

// Categories that can be assigned to SD-WAN Hubs via parent-child dropdown
// Note: sdwan_branch is NOT here - branches are assigned via the dedicated hub assignment table
export const SDWAN_HUB_CHILD_CATEGORIES: ProductCategory[] = ['enterprise_internet'];

// Categories that appear on the LEFT side (Column 1) - customer premises equipment
export const LEFT_SIDE_CATEGORIES: ProductCategory[] = ['last_mile', 'cpe'];

// Categories that appear on the RIGHT side (Column 4) - cloud/internet services
export const RIGHT_SIDE_CATEGORIES: ProductCategory[] = ['cloud_connectivity', 'enterprise_internet'];

// DCI is a special category - connects two ports (neither parent nor child)
export const DCI_CATEGORY: ProductCategory = 'datacenter_interconnect';

/**
 * NRC (Non-Recurring Charges) products should be excluded from service assignment
 * These are one-time fees for implementation/configuration, not actual services
 */
export const isNRCProduct = (sku: string | null): boolean => {
  if (!sku) return false;
  return sku.endsWith('-NRC');
};

/**
 * Format an amount with the order's currency code (e.g. "MXN 2,750").
 * Orders are quoted in different currencies, so a bare "$" is ambiguous.
 * Falls back to USD, matching the order header.
 */
export const formatOrderAmount = (amount: number, currencyCode?: string | null): string => {
  return `${currencyCode || 'USD'} ${amount.toLocaleString()}`;
};

/**
 * Filter out NRC products from order lines for service assignment
 */
export const filterAssignableLines = (lines: OdooOrderLine[]): OdooOrderLine[] => {
  return lines.filter(line => !isNRCProduct(line.product.sku));
};

// Nested parents below a Port: Last Mile -> CPE -> Internet (see hierarchy.ts)
export type NestedParentType = 'last_mile' | 'cpe' | 'cpe_port' | 'internet';

export interface ProductAssignment {
  parentLineId: number;
  childLineIds: number[];
  parentType?: 'port' | 'sdwan_hub' | NestedParentType;
}

export interface AssignmentState {
  assignments: ProductAssignment[];
  unassignedChildren: number[];
}

/**
 * Cloud Connectivity Configuration
 */
export interface CloudConnectivityConfig {
  datacenterId?: string;
  providerId?: string;
  cloudPlatform?: string;
}

/**
 * DCI Port Assignment (Port A and Port B)
 */
export interface DCIPortAssignment {
  portA: number | null;
  portB: number | null;
}

/**
 * Presales Designer Submission Payload
 * Sent to backend to finalize service configuration
 */
export interface PresalesSubmissionPayload {
  odooOrderId: number;
  orderName: string;
  
  // Parent-child service assignments
  assignments: ProductAssignment[];
  
  // Datacenter assignment for each parent port
  parentDatacenters: Record<number, string>; // parentLineId -> datacenterId
  
  // Last Mile addresses
  lastMileAddresses: Record<number, string>; // childLineId -> address
  
  // Cloud Connectivity configurations
  cloudConnectivityConfigs: Record<number, CloudConnectivityConfig>; // childLineId -> config
  
  // DCI (Datacenter Interconnect) port assignments
  dciAssignments: Record<number, DCIPortAssignment>; // dciLineId -> { portA, portB }
  
  // FWaaS assignments (fwaasLineId -> portLineId)
  fwaasToPortAssignments: Record<number, number>;
  
  // Architecture diagram image (base64 data URL)
  architectureImage?: string | null;

  // SD-WAN Hub role assignments (hubLineId -> 'primary' | 'secondary')
  sdwanHubRoles: Record<number, 'primary' | 'secondary'>;

  // SD-WAN Branch primary hub assignments (branchLineId -> primaryHubLineId)
  // Each branch connects to all hubs; this defines which hub is primary for each branch.
  // The other hub(s) automatically become secondary.
  sdwanBranchPrimaryHub: Record<number, number>;

  // Optional presales comment - added to all Jira tickets as an internal comment
  presalesComment?: string;
}

/**
 * Validation result for submission
 */
export interface SubmissionValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Saved Design Payload (from ProcessedOdooOrders.designPayload)
 */
export interface SavedDesignPayload {
  assignments: ProductAssignment[];
  parentDatacenters: Record<number, string>;
  lastMileAddresses: Record<number, string>;
  cloudConnectivityConfigs: Record<number, CloudConnectivityConfig>;
  dciAssignments: Record<number, DCIPortAssignment>;
  fwaasToPortAssignments: Record<number, number>;
  sdwanHubRoles?: Record<number, 'primary' | 'secondary'>;
  sdwanBranchPrimaryHub?: Record<number, number>;
  jiraTickets?: Array<{
    serviceId: string;
    serviceType: string;
    ticketKey: string;
    ticketId?: number;
  }>;
}

/**
 * Design Status Response from GET /api/v1/presales/status/:orderId
 */
export interface DesignStatusResponse {
  odooOrderId: number;
  orderName: string | null;
  status: 'processing' | 'completed' | 'failed' | 'pending_presales' | 'not_processed';
  servicesCreated: number;
  tenantId: number | null;
  isDesignSubmitted: boolean;
  isLegacy?: boolean;
  designSubmittedAt: string | null;
  savedDesign: SavedDesignPayload | null;
  createdAt: string | null;
  completedAt: string | null;
}

/**
 * Existing tenant service (from previous orders)
 */
export interface TenantPortService {
  id: number;
  serviceId: string;
  name: string;
  capacityMbps: number;
  datacenter: { id: string; name: string } | null;
  isDraft: boolean;
  odooOrderId: number | null;
}

export interface TenantParentPort {
  id: number;
  serviceId: string;
  name: string;
  datacenter: { id: string; name: string } | null;
}

export interface TenantInternetService {
  id: number;
  serviceId: string;
  name: string | null;
  mbps: number;
  parentPort: TenantParentPort | null;
  isDraft: boolean;
  odooOrderId: number | null;
}

export interface TenantCloudService {
  id: number;
  serviceId: string;
  name: string | null;
  mbps: number;
  provider: string | null;
  cloudPlatform: string | null;
  parentPort: TenantParentPort | null;
  isDraft: boolean;
  odooOrderId: number | null;
}

export interface TenantServicesResponse {
  success: boolean;
  data: {
    tenant: { id: number; name: string; cid: string };
    services: {
      ports: TenantPortService[];
      internet: TenantInternetService[];
      cloud: TenantCloudService[];
    };
    total: number;
  };
}

